import { zodResolver } from "@hookform/resolvers/zod";
import { Alert, Box, Divider, Typography } from "@mui/material";
import {
  type LicenseSelectionModel,
  getSchema,
} from "Components/Licenses/LicenseOrderForm/schema";
import { CouponInput } from "Components/Licenses/CouponInput/CouponInput";
import { CountrySelector } from "Components/Licenses/CountrySelector/CountrySelector";
import { LicensePicker } from "Components/Licenses/LicensePicker/LicensePicker";
import { licenseQueryOptions } from "Api/Queries/Licenses/useLicenseQuery";
import {
  getNextAvailableLicense,
  isSameAsOrderedLicense,
} from "Components/Licenses/licenseUtils";
import { StepBox } from "Components/Onboarding/shared/StepBox";
import { FullHeightForm } from "Components/Shared/FormStyles";
import { CheckboxInput as CouponCheckbox } from "Components/Shared/Inputs/CheckboxInput";
import { CheckboxInput } from "Components/Shared/Inputs/Form/CheckboxInput";
import { FormInput } from "Components/Shared/Inputs/Form/FormInput";
import { ValuesBox } from "Components/Shared/ValuesBox/ValuesBox";
import { useRegionList } from "Hooks/Options/useRegionList";
import { Resources, useResource } from "Translations/Resources";
import { useQueryClient } from "@tanstack/react-query";
import type { PartyLicenseDto, Region } from "Infrastructure/Api/Api";
import { type ReactNode, useState } from "react";
import { useForm } from "react-hook-form";

const COMPONENT_RESOURCES =
  Resources.Onboarding.BusinessOnboarding.Steps.LicenseSelection.Form;
const COUPON_RESOURCES = Resources.Components.LicenseOrder.Coupon;

type ValidatedCoupon = {
  code: string;
  pricingKey?: string;
};

type Props = {
  onSubmit: (data: LicenseSelectionModel) => void;
  isSubmitting?: boolean;
  defaultValues: Partial<LicenseSelectionModel>;
  isRegionSelectionDisabled?: boolean;
  // Opt in only where an ancestor clips horizontal overflow — never in
  // dialogs/drawers (forces a horizontal scrollbar there).
  hasCarouselEdgeBleed?: boolean;
  // The party's pending unpaid order when the form is revisited; submitting it unchanged
  // must read as "see payment details", not as a new purchase.
  orderedLicense?: PartyLicenseDto;
  // Shown above the coupon (the hunter wizard's affiliate code, ZS-1024).
  affiliateCodeField?: ReactNode;
  // Identifies what the offered prices depend on beyond region and license (an attached
  // affiliate code). A coupon validated under other prices is dropped when it changes.
  pricingKey?: string;
};

export const LicenseOrderForm = ({
  onSubmit,
  isSubmitting,
  defaultValues,
  isRegionSelectionDisabled,
  hasCarouselEdgeBleed,
  orderedLicense,
  affiliateCodeField,
  pricingKey,
}: Props) => {
  const [validatedCoupon, setValidatedCoupon] =
    useState<ValidatedCoupon | null>(null);
  const couponCode =
    validatedCoupon?.pricingKey === pricingKey
      ? (validatedCoupon?.code ?? null)
      : null;
  const setCouponCode = (code: string | null) =>
    setValidatedCoupon(code ? { code, pricingKey } : null);
  const [hasCoupon, setHasCoupon] = useState(false);
  const { t } = useResource();
  const queryClient = useQueryClient();
  const { codeListRegions } = useRegionList();
  // Some market is visible but not open yet — tell the user why it cannot be picked.
  // Disappears on its own once every region is enabled (the launch flip).
  const hasComingSoonRegion = codeListRegions.some((region) => region.disabled);
  const { control, handleSubmit, watch, setValue, getValues } =
    useForm<LicenseSelectionModel>({
      defaultValues,
      resolver: zodResolver(getSchema(t)),
    });

  const isUsingPrivateWallet = watch("isUsingPrivateWallet");
  const currentRegion = watch("region");
  const currentLicenseCode = watch("code");
  const currentWalletAddress = watch("walletAddress");

  const isOrderedLicenseSelected =
    !!orderedLicense && orderedLicense.code === currentLicenseCode;
  const isUnchangedOrder =
    !!orderedLicense &&
    isSameAsOrderedLicense(
      {
        code: currentLicenseCode,
        isUsingPrivateWallet,
        walletAddress: currentWalletAddress,
        couponCode,
      },
      orderedLicense,
    );

  const handleCouponToggle = (checked: boolean) => {
    setHasCoupon(checked);
    if (!checked) {
      setCouponCode(null);
    }
  };

  // After a region switch the license list changes and the selected code may
  // not exist in the new region — resync it to the region's default license.
  const handleRegionChange = async (region: Region) => {
    setCouponCode(null);

    try {
      const { items } = await queryClient.ensureQueryData(
        licenseQueryOptions(region),
      );
      const isCurrentCodeValid = items?.some(
        (license) => license.code === getValues("code"),
      );
      if (!isCurrentCodeValid) {
        const fallbackCode = getNextAvailableLicense(items ?? [])?.code;
        if (fallbackCode) {
          setValue("code", fallbackCode);
        }
      }
    } catch {
      // Region licenses failed to load — LicensePicker renders its own error
      // state for the same query, nothing to resync here.
    }
  };

  const handleFormSubmit = (data: LicenseSelectionModel) =>
    onSubmit({ ...data, couponCode });

  return (
    <FullHeightForm onSubmit={handleSubmit(handleFormSubmit)}>
      <StepBox
        submitButtonLabel={t(
          isUnchangedOrder
            ? COMPONENT_RESOURCES.SeePaymentDetails
            : COMPONENT_RESOURCES.SubmitButton,
        )}
        submitButtonDescription={
          isOrderedLicenseSelected && (
            <Alert severity="info">
              {t(COMPONENT_RESOURCES.AlreadyOrdered)}
            </Alert>
          )
        }
        isSubmitting={isSubmitting}
      >
        <CountrySelector
          control={control}
          isDisabled={!!isRegionSelectionDisabled}
          onRegionChange={handleRegionChange}
        />
        {hasComingSoonRegion && (
          <Typography variant="body2" color="text.secondary">
            {t(Resources.Components.CountrySelector.SlovakLicensesComingSoon)}
          </Typography>
        )}
        <ValuesBox title={t(Resources.Common.License)}>
          <Typography>{t(COMPONENT_RESOURCES.ChooseLicense)}</Typography>
          {/* Gate on the live form value, not the static defaultValues prop — a user
              who starts without a region gets a code only after picking one (the
              region-change handler resyncs it), and the picker must appear then. */}
          {currentLicenseCode ? (
            <LicensePicker
              onSelect={(code) => {
                if (code !== currentLicenseCode && couponCode) {
                  setCouponCode(null);
                }
                setValue("code", code);
              }}
              defaultValue={defaultValues.code}
              currentValue={currentLicenseCode}
              region={currentRegion}
              hasEdgeBleed={hasCarouselEdgeBleed}
            />
          ) : (
            t(
              currentRegion
                ? COMPONENT_RESOURCES.NoLicensesAvailableForCountry
                : COMPONENT_RESOURCES.SelectCountryFirst,
            )
          )}
        </ValuesBox>
        <Typography
          variant="caption"
          fontWeight={300}
          textAlign="center"
          display="block"
        >
          {t(COMPONENT_RESOURCES.PriceIncludesVAT)}
        </Typography>
        <Divider sx={{ mt: 5 }} />
        {affiliateCodeField && (
          <>
            <ValuesBox>{affiliateCodeField}</ValuesBox>
            <Divider sx={{ mt: 2 }} />
          </>
        )}
        {currentLicenseCode && (
          <ValuesBox>
            <CouponCheckbox
              name="hasCoupon"
              checked={hasCoupon}
              onChange={(_, checked) => handleCouponToggle(checked)}
              label={t(COUPON_RESOURCES.CheckboxLabel)}
            />
            {hasCoupon && (
              <CouponInput
                key={`${currentRegion}-${currentLicenseCode}-${pricingKey ?? ""}`}
                licenseCode={currentLicenseCode}
                region={currentRegion}
                onCouponValidated={setCouponCode}
              />
            )}
          </ValuesBox>
        )}
        <Divider sx={{ mt: 2 }} />
        <ValuesBox>
          <CheckboxInput
            control={control}
            name="isUsingPrivateWallet"
            label={t(COMPONENT_RESOURCES.WalletCheckboxLabel)}
          />
          {isUsingPrivateWallet && (
            <Box display="flex" flexDirection="column" gap={2}>
              <Typography>
                {t(COMPONENT_RESOURCES.PrivateWalletAddressDescription)}
              </Typography>
              <FormInput
                type="text"
                control={control}
                label={t(COMPONENT_RESOURCES.PrivateWalletAddress)}
                name="walletAddress"
              />
            </Box>
          )}
        </ValuesBox>
        <Divider sx={{ my: 2 }} />
      </StepBox>
    </FullHeightForm>
  );
};
